import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #checkoutObserver = null;

  /** Shiprocket / checkout overlay ko pehchanne ke liye regex */
  #checkoutRegex = /shiprocket|sr-checkout|srcheckout|checkout-modal|fastrr|gokwik/i;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // 1) Checkout overlay/iframe body me add hote hi drawer band karo
    this.#checkoutObserver = new MutationObserver(this.#handleBodyMutations);
    this.#checkoutObserver.observe(document.body, { childList: true });

    // 2) Fallback: Buy Now / checkout button click par delay ke saath band karo
    this.addEventListener('click', this.#handleCheckoutClick, true);

    // 3) Fallback: checkout iframe se postMessage aaye to band karo
    window.addEventListener('message', this.#handleWindowMessage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('click', this.#handleCheckoutClick, true);
    window.removeEventListener('message', this.#handleWindowMessage);

    this.#checkoutObserver?.disconnect();
    this.#checkoutObserver = null;
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Body me naya element aaye aur wo checkout overlay ho to drawer close karo.
   * @param {MutationRecord[]} mutations
   */
  #handleBodyMutations = (mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (this.#isCheckoutElement(/** @type {HTMLElement} */ (node))) {
          this.close();
          return;
        }
      }
    }
  };

  /**
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  #isCheckoutElement(el) {
    const idClass = `${el.id || ''} ${typeof el.className === 'string' ? el.className : ''}`;
    if (this.#checkoutRegex.test(idClass)) return true;

    if (el.tagName === 'IFRAME') {
      const src = /** @type {HTMLIFrameElement} */ (el).src || '';
      if (this.#checkoutRegex.test(src)) return true;
    }

    // Wrapper ke andar iframe ho to bhi pakdo
    const iframe = el.querySelector?.('iframe');
    if (iframe && this.#checkoutRegex.test(iframe.src || '')) return true;

    return false;
  }

  /**
   * Buy Now / checkout button click hone par thoda delay de kar drawer band karo,
   * taaki checkout ka apna handler pehle chal jaye.
   * @param {Event} event
   */
  #handleCheckoutClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const btn = target.closest?.(
      '[name="checkout"], .cart__checkout-button, [class*="buy-now"], [class*="shiprocket"], [id*="shiprocket"], [class*="rocket"], [class*="gokwik"]'
    );
    if (!btn) return;

    setTimeout(() => this.close(), 500);
  };

  /**
   * @param {MessageEvent} event
   */
  #handleWindowMessage = (event) => {
    try {
      const data = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
      if (data && this.#checkoutRegex.test(data) && /open|init|load/i.test(data)) {
        this.close();
      }
    } catch (e) {
      // ignore non-serializable messages
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}